import React from 'react';
import { usePerformanceSentinel } from '../../hooks/usePerformanceSentinel';

export interface ButtonProps {
    /** Button style variant */
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
    /** Button size */
    size?: 'sm' | 'md' | 'lg';
    /** Button content */
    children: React.ReactNode;
    /** Optional link href - renders as anchor if provided */
    href?: string;
    /** Target attribute for links */
    target?: string;
    /** Rel attribute for links */
    rel?: string;
    /** Click handler */
    onClick?: () => void;
    /** Additional CSS classes */
    className?: string;
    /** Disabled state */
    disabled?: boolean;
    /** Button type for form submission */
    type?: 'button' | 'submit' | 'reset';
    /** Custom style object */
    style?: React.CSSProperties;
}

const baseStyles = 'inline-flex items-center justify-center font-semibold rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-bg disabled:opacity-50 disabled:pointer-events-none';

const variantStyles = {
    primary: 'bg-accent text-white shadow-accent-glow hover:bg-accent-hover',
    secondary: 'bg-surface-light text-text border border-border hover:bg-white/10',
    outline: 'border-2 border-accent text-accent hover:bg-accent hover:text-white',
    ghost: 'text-muted hover:text-text hover:bg-surface-light',
};

const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
};

/**
 * Reusable Button component with multiple variants and sizes.
 * Renders as an anchor when href is provided, otherwise as a button.
 */
export function Button({
    variant = 'primary',
    size = 'md',
    children,
    href,
    target,
    rel,
    onClick,
    className = '',
    disabled = false,
    type = 'button',
    style,
    ...props
}: ButtonProps & React.ComponentPropsWithoutRef<'button' | 'a'>) {
    const { isPanic } = usePerformanceSentinel();
    const transitionStyles = isPanic ? '' : 'transition-colors';
    const combinedClassName = `${baseStyles.replace('transition-colors', '')} ${transitionStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;

    if (href) {
        return (
            <a
                href={href}
                target={target}
                rel={rel}
                className={combinedClassName}
                style={style}
                {...(props as React.ComponentPropsWithoutRef<'a'>)}
            >
                {children}
            </a>
        );
    }

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={combinedClassName}
            style={style}
            {...(props as React.ComponentPropsWithoutRef<'button'>)}
        >
            {children}
        </button>
    );
}
